import React, { useEffect, useMemo, useState } from "react";
import { Alert, Modal, Input, Button, Select, Spin, message } from "antd";
import { Row, Col } from "antd";
import { deferredExamService } from "../../../services/deferredExam";
import {
  buildExamOptions,
  filterExamOption,
  renderExamOptions,
  toggleGroupExamIds,
} from "./utils/examGroupingUtils";

const { TextArea } = Input;

const DEFAULT_NOTIFICATION_CONTENT = `1. Reevaluación por Oftalmología
Acuda a nuestra sede llevando sus lentes correctores (lentes de medida) para realizar nuevamente la evaluación de su agudeza visual.

2. Evaluación de Audiometría
Antes de acudir, deberá realizarse un lavado de oído. Una vez realizado este procedimiento, podrá acercarse a nuestra sede para rendir el examen de audiometría.

3. Evaluación de Espirometría
Deberá acudir cuando haya finalizado completamente su proceso respiratorio (gripe, tos, resfrío u otra afección respiratoria), para poder realizar el examen de espirometría en condiciones adecuadas.

Horario de atención:

Lunes a viernes: 7:00 a. m. a 1:00 p. m.
Sábados: 7:00 a. m. a 12:00 p. m.`;

const DeferredExamModal = ({
  medicalConsultationDetailId,
  isModalVisible,
  setIsModalVisible,
  serviceDetail,
  onPrescriptionSaved,
}) => {
  const [notificationContent, setNotificationContent] = useState(DEFAULT_NOTIFICATION_CONTENT);
  const [loading, setLoading] = useState({ fetch: false, save: false });
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const [prescriptionState, setPrescriptionState] = useState(null);

  const observedExams = useMemo(
    () => serviceDetail?.filter(exam => exam.exam_id) || [],
    [serviceDetail]
  );
  const examOptions = useMemo(() => buildExamOptions(observedExams, true), [observedExams]);
  const isSent = prescriptionState === 'sent';
  const isFormInvalid = !notificationContent?.trim() || selectedExamIds.length === 0;

  const resetFormToDefaults = () => {
    setNotificationContent(DEFAULT_NOTIFICATION_CONTENT);
    setSelectedExamIds([]);
    setPrescriptionState(null);
  };

  const fetchLatestPrescription = async () => {
    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      const response = await deferredExamService.getLatestPrescription(medicalConsultationDetailId);
      if (response?.data?.prescription) {
        setPrescriptionState(response.data.prescription.state);
        const extraData = response.data.prescription.extra_data?.[0];
        if (extraData?.notification_content) {
          setNotificationContent(extraData.notification_content);
        }
        if (extraData?.selected_exam_ids) {
          setSelectedExamIds(extraData.selected_exam_ids);
        }
      } else {
        resetFormToDefaults();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  useEffect(() => {
    if (isModalVisible.deferredExam && medicalConsultationDetailId) {
      fetchLatestPrescription();
    }
  }, [isModalVisible.deferredExam, medicalConsultationDetailId]);

  const handleClickGroupTitle = (groupExams) => {
    setSelectedExamIds((selectedExamIds) => toggleGroupExamIds(selectedExamIds, groupExams));
  };

  const closeModal = () => {
    setIsModalVisible(prev => ({ ...prev, deferredExam: false }));
  };

  const handleSave = async () => {
    try {
      setLoading(prev => ({ ...prev, save: true }));

      await deferredExamService.savePrescription({
        medicalConsultationDetailId,
        notificationContent,
        selectedExamIds,
      });

      message.success({
        content: "Borrador de reevaluación guardado exitosamente",
        style: { marginTop: "35vh" },
      });

      if (onPrescriptionSaved) {
        onPrescriptionSaved();
      }

      closeModal();
    } catch (error) {
      console.error(error);
      message.error({
        content: "Error al guardar la reevaluación. Intente nuevamente.",
        style: { marginTop: "35vh" },
      });
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  return (
    <Modal
      visible={isModalVisible.deferredExam}
      onCancel={closeModal}
      centered
      closable
      footer={null}
      width={650}
      title={null}
      className="prescriptions-modal deferred-exam-modal"
    >
      {loading.fetch ? (
        <Spin className="cmt-spin custom-spinner mt-50 mb-50" />
      ) : (
        <Row className="p-40">
          {isSent && (
            <Col lg={24} xs={24}>
              <Alert
                className="cmt-alert cmt-alert-warning mb-20"
                type="warning"
                message={
                  <span>
                    <strong>Esta reevaluación ya fue enviada.</strong> Para modificarla, elimínela desde el historial y genere una nueva.
                  </span>
                }
              />
            </Col>
          )}

          <Col lg={24} xs={24}>
            <h2 className="mb-10">Notificación de Reevaluación</h2>
            <p className="mb-30">
              Redacte el correo para notificar a la empresa y paciente, según la configuración empresa
            </p>
          </Col>

          <Col lg={24} xs={24} className="mb-20">
            <p className="mb-5">Seleccionar examen(es) a reevaluar</p>
            <Select
              mode="multiple"
              className="cmt-select width-100"
              popupClassName="grouped-exams-dropdown"
              placeholder="Seleccione los exámenes"
              value={selectedExamIds}
              onChange={setSelectedExamIds}
              disabled={isSent}
              showSearch
              filterOption={filterExamOption}
            >
              {renderExamOptions({
                examOptions,
                selectedExamIds,
                onGroupTitleClick: handleClickGroupTitle,
              })}
            </Select>
          </Col>

          <Col lg={24} xs={24} className="mb-20">
            <TextArea
              value={notificationContent}
              onChange={(event) => setNotificationContent(event.target.value)}
              className="cmt-input deferred-exam-textarea"
              disabled={isSent}
            />
          </Col>

          <Col lg={24} xs={24}>
            <Button
              loading={loading.save}
              onClick={handleSave}
              className={`cmt-button primary ${isFormInvalid || isSent ? 'disabled' : ''}`}
              type="primary"
              size="large"
              block
              disabled={isFormInvalid || isSent}
            >
              Guardar
            </Button>
          </Col>
        </Row>
      )}
    </Modal>
  );
};

export default DeferredExamModal;
